import { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';

import { PhoneCall } from '../../../resources/phoning/types';
import { DossierId } from '../../../resources/dossier/types';

const RESIZABLE_CONTENT: Record<string, boolean> = {
    contract: true,
    'phone-call-details': false,
    'note-editor': false,
};

export const DRAWER_WIDTH_NORMAL = 420;
export const DRAWER_WIDTH_EXPANDED = 960;

type SidePanelState =
    | { type: 'contract'; data?: never }
    | { type: 'phone-call-details'; data: PhoneCall }
    | { type: 'note-editor'; data: { dossierId: DossierId } };

type PanelControlArgs =
    | { contentType: 'contract' }
    | { contentType: 'phone-call-details'; data: PhoneCall }
    | { contentType: 'note-editor'; data: { dossierId: DossierId } };

type PanelControlFunction = (args: PanelControlArgs) => void;

type SidePanelContextValue = {
    isOpen: boolean;
    isResizable: boolean;
    isExpanded: boolean;
    panelState: SidePanelState;
    drawerWidth: number;
    openPanel: PanelControlFunction;
    closePanel: () => void;
    toggleExpanded: () => void;
};

const SidePanelContext = createContext<SidePanelContextValue | undefined>(undefined);

type SidePanelProviderProps = {
    children: ReactNode;
};

export const SidePanelProvider = ({ children }: SidePanelProviderProps) => {
    const [panelState, setPanelState] = useState<SidePanelState>({ type: 'contract' });
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const isResizable = useMemo(() => {
        return panelState.type ? RESIZABLE_CONTENT[panelState.type] === true : false;
    }, [panelState.type]);

    const drawerWidth = useMemo(() => {
        if (!isResizable) return DRAWER_WIDTH_NORMAL;
        return isExpanded ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_NORMAL;
    }, [isResizable, isExpanded]);

    const openPanel = useCallback((args: PanelControlArgs) => {
        switch (args.contentType) {
            case 'contract':
                setPanelState({ type: 'contract' });
                break;
            case 'phone-call-details':
                setPanelState({ type: 'phone-call-details', data: args.data });
                break;
            case 'note-editor':
                setPanelState({ type: 'note-editor', data: args.data });
                break;
        }
        setIsOpen(true);
    }, []);

    const closePanel = useCallback(() => {
        setIsOpen(false);
        setIsExpanded(false);
    }, []);

    const toggleExpanded = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const value = useMemo(
        () => ({
            isOpen,
            isExpanded,
            isResizable,
            drawerWidth,
            panelState,
            openPanel,
            closePanel,
            toggleExpanded,
        }),
        [
            panelState,
            isOpen,
            isExpanded,
            isResizable,
            drawerWidth,
            openPanel,
            closePanel,
            toggleExpanded,
        ],
    );

    return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

/**
 * Hook to access SidePanel context. Throws an error if used outside SidePanelProvider.
 * Use this in new components where the provider is guaranteed to exist.
 */
export const useSidePanel = (): SidePanelContextValue => {
    const context = useContext(SidePanelContext);
    if (context === undefined) {
        throw new Error('useSidePanel must be used within a SidePanelProvider');
    }
    return context;
};

/**
 * Optional version of useSidePanel that returns undefined if used outside the provider.
 * Use this for backward compatibility in legacy components.
 */
export const useSidePanelOptional = (): SidePanelContextValue | undefined => {
    const context = useContext(SidePanelContext);
    return context;
};
